// Copyright (c) Docugami, Inc. All rights reserved.

// TODO future
// free up unused data to minimize memory usage
// code coverage
// change inserted / deleted to left / right
// after LCS, find huge deleted/inserted sequences, and run LCS on them, to detect moved blocks of text
// regression tests
// wide range of tests
// what happens when user changes text
// getDocugamiIdFromPElement - should get text from just specific field. also should work in case the field is either simple form or complex form.

import { ComparisonUnitAtom } from "./ComparisonUnitAtom";
import { DgXmlNames } from "./DgXmlNames";
import { ComparisonUnitToken } from "./ComparisonUnitToken";
import { CorrelatedSequence } from "./CorrelatedSequence";
import { CorrelationStatus } from "./CorrelationStatus";
import { DgOnSelectionEventArgs } from "./DgOnSelectionEventArgs";
import { LcsSettings } from "./LcsSettings";
import { lcs } from "./Lcs";
import { getDocugamiIdFromPElement, updateDocugamiIdInPElement, removeMetadata, addSpanIdToParagraphMetadata, getKnownChunksLocal, getParagraphMetadataFromPElement } from "./DgParagraphMetadataUtil";
import { ParagraphInfo } from "./DgParagraphInfo";
import { isTokenBreakingCharacter } from "./DgTokenBreakingCharacters";
import { IDgWordCanvasProxyCallbacks } from "./worker/worker";
import { DOMParser } from '@xmldom/xmldom'
import { StringBuilder } from "utils/StringBuilder";
import { DgKnownChunk, DgKnownChunkType } from "./DgParagraphMetadata";
import { getElementTextValue } from "./DgGenerateFormattedContent";
import { Logger, LoggerCategory } from 'utils/Logger';


/*eslint-disable */

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require('../../../../../utils/linq');
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');
const { v4: uuidv4 } = require('uuid');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
const groupAdjacent = openXml.groupAdjacent;
/*eslint-enable */

// This is a class used locally in this module only.  It is not part of a public API.
class TokenParagraphPair
{
    public token: ComparisonUnitToken;
    public uuid: string;
    constructor(token: ComparisonUnitToken, uuid: string)
    {
        this.token = token;
        this.uuid = uuid;
    }
}

export class DgWordCanvas
{
    // public fields that persist as state
    public openXml: any; // Ltxml XElement
    public dgml: any; // ltxml XElement
    public correlatedSequenceList: CorrelatedSequence[] | null = null;
    public paragraphInfoList: any;
    public fieldSpanIdList: string[];

    // temp storage of span ids, for verification of population of task pane cards
    public static replacedSpanId: string|null = null;
    public static replacingSpanId: string|null = null;
    public static deletedSpanId: string|null = null;
    public static insertedSpanId: string|null = null;
    public static actionCount: number = 0;

    private _defaultRunProperties: any | null = null;
    public get defaultRunProperties() {
        if (this._defaultRunProperties === null) {
            const mostCommonRunProperties = this.openXml
                .descendants(W.r)
                .select((r: any) => {
                    const rPr = r.element(W.rPr);
                    if (!!rPr) {
                        const cloned_rPr = new XElement(W.rPr,
                            new XAttribute(XNamespace.xmlns + "w", wNs.namespaceName),
                            rPr.element(W.rFonts),
                            rPr.element(W.sz));
                        const cs = cloned_rPr.toString();
                        return cs;
                    }
                    return null;
                })
                .where((z: any) => z != null)
                .groupBy((z: any) => z)
                .orderByDescending((z: any) => z.count())
                .firstOrDefault()
                .first();
            this._defaultRunProperties = XElement.parse(mostCommonRunProperties);
        }
        return this._defaultRunProperties;
    }

    constructor(dgml: string, fieldSpanIdList: string[])
    {
        // dump DGML to console
        if (LcsSettings.lcsDebugFalse) {
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, dgml);
        }

        try {
            dgml = DgWordCanvas.dgmlFixup(dgml);
            this.dgml = XElement.parse(dgml);
            DgWordCanvas.removeOutflowChunks(this.dgml);
        } catch (e) {
            console.error(e)
        }

        this.fieldSpanIdList = fieldSpanIdList;
    }


    /**
     * Returns whether the canvas currently contains the specified chunk.
     * @param spanId The chunk ID to find.
     */
    public async isChunkOnCanvas(spanId: string, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<boolean>
    {
        const correlatedSequenceList: CorrelatedSequence[] = this.correlatedSequenceList as CorrelatedSequence[];
        for (let i = 0; i < correlatedSequenceList.length; ++i)
        {
            const correlatedSequence = correlatedSequenceList[i];
            if (correlatedSequence.correlationStatus === CorrelationStatus.Equal) {
                const pairs = Enumerable.from(correlatedSequence.comparisonUnitArray1)
                    .zip(Enumerable.from(correlatedSequence.comparisonUnitArray2),
                    (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) =>
                    {
                        return {
                            pu1: pu1,
                            pu2: pu2,
                        };
                    })
                    .toArray();
                for (let j = 0; j < pairs.length; ++j)
                {
                    const dgmlToken = pairs[j].pu2 as ComparisonUnitToken;

                    // It isnt just the spanId at the leaf.  It can match any of the ancestors.
                    const dgmlFirstAtom = dgmlToken.contents[0] as ComparisonUnitAtom;
                    const dgmlTreeElement = dgmlFirstAtom.contentElement;
                    const relevantChunk = dgmlTreeElement.ancestorsAndSelf()
                        .firstOrDefault((de: any) =>
                            {
                                let spanId2 = de.attribute(DgXmlNames.spanId)?.value || "";
                                return spanId === spanId2;
                            });

                    if (relevantChunk === null) {
                        continue;
                    }

                    const docxToken = pairs[j].pu1 as ComparisonUnitToken;

                    // have the guid on the docxToken
                    const docxFirstAtom = docxToken.contents[0] as ComparisonUnitAtom;
                    const docxTreeElement = docxFirstAtom.contentElement;
                    const para = docxTreeElement.ancestorsAndSelf(W.p).firstOrDefault();
                    const paraUuid = getDocugamiIdFromPElement(para);
                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `Looking for paraUuid: ${paraUuid}`);

                    // now find out if the paragraph is still in the document.
                    // get paragraphs with hidden guids
                    const OOXML = await proxyCallbacks.getBodyOoxml();
                    const root = XElement.parse(OOXML);

                    const paragraphs = root.descendants(W.p).toArray();
                    const foundParagraph = Enumerable.from(paragraphs)
                        .any((p: any, i: number) => {
                            const guidStr = getDocugamiIdFromPElement(p) as string;
                            if (guidStr !== null && paraUuid === guidStr)
                            {
                                return true;
                            }
                            return false;
                        });

                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `foundParagraph: ${foundParagraph}`);
                    return true;
                }
            }
        }

        return false;
    }

    public async getKnownChunks(proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<DgKnownChunk[]|null>
    {
        const OOXML_text = await proxyCallbacks.getBodyOoxml()

        const ooxml = XElement.parse(OOXML_text);
        const knownChunksArray = getKnownChunksLocal(ooxml, this.dgml);

        let hasError = false;
        if (DgWordCanvas.replacedSpanId !== null) {
            if (DgWordCanvas.replacedSpanId === DgWordCanvas.replacingSpanId) {
                Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks: alerting chunk ${DgWordCanvas.replacedSpanId} was replaced with another chunk with the same span ID, what is going on Rutu?`, '');
            }
            else {
                const replacedChunkExists: boolean = knownChunksArray.some(kc => kc.Id === DgWordCanvas.replacedSpanId && kc.IsAncestor === false);
                const replacedChunkExistsBecauseAddedAsAncestor: boolean = knownChunksArray.some(kc => kc.Id === DgWordCanvas.replacedSpanId && kc.IsAncestor === true);
                if (replacedChunkExists) {
                    Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks: alerting chunk ${DgWordCanvas.replacedSpanId} was replaced but still is in document`, '');
                    hasError = true;
                }
                else if (replacedChunkExistsBecauseAddedAsAncestor) {
                    Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks: alerting chunk ${DgWordCanvas.replacedSpanId} was replaced but a child element still is in document, what is going on Rutu?`, '');
                }
                else {
                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `getKnownChunks: confirming chunk ${DgWordCanvas.replacedSpanId} was replaced in document`);
                }
            }
        }
        if (DgWordCanvas.replacingSpanId !== null) {
            const replacingChunkExists: boolean = knownChunksArray.some(kc => kc.Id === DgWordCanvas.replacingSpanId);
            if (!replacingChunkExists) {
                Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks: alerting chunk ${DgWordCanvas.replacingSpanId} replaced ${DgWordCanvas.replacedSpanId} but is not in document`, '');
                hasError = true;
            }
            else {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, `getKnownChunks: confirming chunk ${DgWordCanvas.replacingSpanId} is in document`);
            }
        }
        if (DgWordCanvas.deletedSpanId !== null) {
            const deletedChunkExists: boolean = knownChunksArray.some(kc => kc.Id === DgWordCanvas.deletedSpanId);
            if (deletedChunkExists) {
                Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks: alerting chunk ${DgWordCanvas.deletedSpanId} was deleted but still is in document`, '');
                hasError = true;
            }
            else {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, `getKnownChunks: confirming chunk ${DgWordCanvas.deletedSpanId} was deleted from document`);
            }
        }
        if (DgWordCanvas.insertedSpanId !== null) {
            const insertedChunkExists: boolean = knownChunksArray.some(kc => kc.Id === DgWordCanvas.insertedSpanId);
            if (!insertedChunkExists) {
                Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks: alerting chunk ${DgWordCanvas.insertedSpanId} was inserted but is not in document`, '');
                hasError = true;
            }
            else {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, `getKnownChunks: confirming chunk ${DgWordCanvas.insertedSpanId} has been inserted into document`);
            }
        }
        DgWordCanvas.replacedSpanId = null;
        DgWordCanvas.replacingSpanId = null;
        DgWordCanvas.deletedSpanId = null;
        DgWordCanvas.insertedSpanId = null;
        if (hasError) {
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, ooxml.toString());
        }

        if (!!knownChunksArray)
        {
            return knownChunksArray;
        }
        return [];
    }

    public sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    public async establishDgmlCorrelation(proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<boolean>
    {
        // if I need to delay so that I can open devtools to hit a breakpoint
        if (LcsSettings.lcsDebugFalse)
        {
            await this.sleep(5000);
            // debugger; // sometimes want to break into the debugger at this point, sometimes not.
        }

        Logger.LogDebug(LoggerCategory.AssistedAuthoring, 'establishDgmlCorrelation entry');

        proxyCallbacks.bucketTimer(1, "800-WORD-getOoxml");
        const OOXML = await proxyCallbacks.getBodyOoxml()

        proxyCallbacks.bucketTimer(2, "100-est/010-Ltxml-parse");
        this.openXml = XElement.parse(OOXML);

        // #ParagraphGuid check if necessary to clean the document / remove fields

        proxyCallbacks.bucketTimer(2, "100-est/011-Check-Already-Processed");

        this.openXml = await removeMetadata(this.openXml, proxyCallbacks);

        proxyCallbacks.bucketTimer(1, "020-corr");
        // we totally change the tree in this method, have to send it back.

        const resultCorrelate = await DgWordCanvas.correlateXmlTrees(this.openXml, this.dgml, proxyCallbacks);
        const thisCorrelatedSequence = resultCorrelate[0];
        const newFlatOpc = resultCorrelate[1];
        const newDgml = resultCorrelate[2];

        // add hidden paragraph uuids here
        const body = this.openXml.descendants(W.body).firstOrDefault();
        if (body !== null) {
            addHiddenParagraphUuids(body);
        }

        // create list of tokens, by paragraph, where the key is the paragraph guid
        proxyCallbacks.bucketTimer(2, "100-est/030-para-tok-list");
        this.createTokenListByParagraph(thisCorrelatedSequence, proxyCallbacks); // this is the first place where we actually use the paragraph guid

        this.correlatedSequenceList = thisCorrelatedSequence;
        this.openXml = newFlatOpc;
        this.dgml = newDgml;

        // correlate tokens, setting correspondingToken for all correlated docx and dgml tokens
        proxyCallbacks.bucketTimer(2, "100-est/060-correlateTokens");
        this.correlateTokensBetweenDocxAndDgml();

        // iterate through all paragraphs, determine docugami spanId, add to paragraph metadata
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, `setDgmlSpanIdMetadataForAllParagraphs`);

        this.setDgmlSpanIdMetadataForAllParagraphs();

        // NOTE: paintCanvas does not work for Word online.  It prevents creation of content controls.
        //       If you want full fidelity of painting of the canvas, also need to enable tokenizing of runs in createComparisonUnitAtomListFromXml.
        //       Note that this does NOT work for Word online.  Tokenizing of runs will also prevent creation of content controls in Word online

        // this.paintCanvas();

        const newOoxml = this.getOoxml(proxyCallbacks);

        proxyCallbacks.bucketTimer(1, "810-WORD-insertOoxml");

        await proxyCallbacks.replaceBodyOoxml(newOoxml);
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, "entire document ooxml replaced");

        return true;
    }

    public async getSelectedParaInfo(paragraphUuid: string, expandToAncestor: boolean): Promise<null | DgOnSelectionEventArgs>
    {
        if (!this.paragraphInfoList) {
            return null;
        }

        const paraInfo = this.paragraphInfoList[paragraphUuid] as any;

        if (!paraInfo) {
            // Note that we may not find the paragraph when inserting content.  The new content has a new GUID on it,
            // which will not be in the paragraphInfoList object.

            // However, in the case where we really do expect to find a paragraph, the following code can help
            // to illuminate why not finding paragraphUuid in paragraphInfoList.

            // let db = "";
            // db = db + `looking for paragraph: >${paragraphUuid}<\n`
            // Logger.LogDebug(LoggerCategory.AssistedAuthoring, db);
            // Object.keys(dgWordCanvas.paragraphInfoList).forEach(function(key,index) {
            //     // key: the name of the object key
            //     // index: the ordinal position of the key within the object
            //     Logger.LogDebug(LoggerCategory.AssistedAuthoring, `${index}: >${key}<\n`);
            // });

            Logger.LogDebug(LoggerCategory.AssistedAuthoring, `processSelectEvent did not find paraGuid ${paragraphUuid} so no event generated`);
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, `It is possible that the paragraph was just inserted.`);
            return null;
        }

        if (!paraInfo.comparisonUnitTokenList === null) {
            return null;
        }

        const theParagraphDocxToken = (paraInfo.comparisonUnitTokenList as ComparisonUnitToken[])[0];
        const dgmlParagraphToken = theParagraphDocxToken.correspondingToken;
        const dgmlParagraphFirstAtom = (dgmlParagraphToken as ComparisonUnitToken).contents[0] as ComparisonUnitAtom;
        const dgmlParagraphTreeElement = dgmlParagraphFirstAtom.contentElement;
        let paragraphSpanId = dgmlParagraphTreeElement.attribute(DgXmlNames.spanId)?.value || "";

        let previousParagraphSpanId: string | null = null;
        const previousParaInfo = paraInfo.previousParagraphInfo;
        if (!!previousParaInfo) {
            const thePreviousParagraphDocxToken = (previousParaInfo.comparisonUnitTokenList as ComparisonUnitToken[])[0];
            const dgmlPreviousParagraphToken = thePreviousParagraphDocxToken.correspondingToken;
            const dgmlPreviousParagraphFirstAtom = (dgmlPreviousParagraphToken as ComparisonUnitToken).contents[0] as ComparisonUnitAtom;
            const dgmlPreviousParagraphTreeElement = dgmlPreviousParagraphFirstAtom.contentElement;
            previousParagraphSpanId = dgmlPreviousParagraphTreeElement.attribute(DgXmlNames.spanId)?.value || "";
        }

        if (paragraphSpanId !== "") {
            if (expandToAncestor) {
                paragraphSpanId = this.getHighestAncestorOrSelfSpanIdWithSameStartToken(paragraphSpanId);
            }

            const eventArgs: DgOnSelectionEventArgs = {
                DgmlSpanId: paragraphSpanId,
                ParagraphDgmlSpanId: paragraphSpanId,
                PreviousParagraphDgmlSpanId: this.getHighestAncestorOrSelfSpanIdWithSameStartToken(previousParagraphSpanId),
                IsField: false,
                SelectionLength: 0
            };
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, `DgWordCanvas triggering onSelection event isField: ${eventArgs.IsField} DgmlSpanId: ${eventArgs.DgmlSpanId} ParagraphDgmlSpanId: ${eventArgs.ParagraphDgmlSpanId} PreviousParagraphDgmlSpanId: ${eventArgs.PreviousParagraphDgmlSpanId}`);
            return eventArgs;
        }

        return null;
    }

    public async getContextSpanIds(spanId: string): Promise<string[]>
    {
        try {
            let selectedChunk = this.dgml
                .descendantsAndSelf(DgXmlNames.chunk)
                .firstOrDefault((ch:any) => ch.attribute(DgXmlNames.spanId)?.value === spanId);

            const container = selectedChunk
                .ancestorsAndSelf(DgXmlNames.chunk)
                .firstOrDefault((ch: any) => ch.attribute("labels")?.value.includes("cp-container"));

            if (container) {
                selectedChunk = container;
            }

            const spanIds = selectedChunk
                .descendantsAndSelf(DgXmlNames.chunk)
                .where((ch:any) => ch.attribute("id")?.value.includes("hline") || ch.attribute("struct_class"))
                .select((ch:any) => ch.attribute(DgXmlNames.spanId)?.value)
                .toArray()

            return spanIds;
        }
        catch (error) {
            console.error(error);
        }

        return [];
    }

    // create list of tokens, by paragraph, where the key is the paragraph guid

    // for each token, project pair of token, and paragraph guid
    // groupAdjacent by paragraph guid
    // take group of groups, put into documentParagraphList as array
    private createTokenListByParagraph(thisCorrelatedSequence: any, proxyCallbacks: IDgWordCanvasProxyCallbacks)
    {
        const tokensWithParagraph: TokenParagraphPair[] = [];
        for (let i = 0; i < thisCorrelatedSequence.length; ++i) {
            const correlatedSequence = thisCorrelatedSequence[i];
            if (correlatedSequence.correlationStatus === CorrelationStatus.Equal) {
                const pairs = Enumerable.from(correlatedSequence.comparisonUnitArray1)
                    .zip(Enumerable.from(correlatedSequence.comparisonUnitArray2),
                        (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) => {
                            return {
                                pu1: pu1,
                                pu2: pu2,
                            };
                        })
                    .toArray();
                for (let j = 0; j < pairs.length; ++j) {
                    const docxToken = pairs[j].pu1 as ComparisonUnitToken;
                    const docxFirstAtom = docxToken.contents[0] as ComparisonUnitAtom;
                    const docxTreeElement = docxFirstAtom.contentElement;
                    const para = docxTreeElement.ancestorsAndSelf(W.p).firstOrDefault();
                    const paraUuid = getDocugamiIdFromPElement(para);
                    if (!paraUuid) {
                        continue;
                    }
                    const twp = new TokenParagraphPair(docxToken, paraUuid);
                    tokensWithParagraph.push(twp);
                }
            }
        }

        proxyCallbacks.bucketTimer(2, "100-est/040-group-adj");
        const grouped = groupAdjacent(tokensWithParagraph, (t: any) => t.uuid);

        // now create paragraphInfoList
        proxyCallbacks.bucketTimer(2, "100-est/050-para-info-list");
        this.paragraphInfoList = {};
        for (let i = 0; i < grouped.length; ++i) {
            const pairs = grouped[i];
            const tokens = pairs.Group.toArray();
            const docxToken = tokens[0].token;
            const docxFirstAtom = docxToken.contents[0] as ComparisonUnitAtom;
            const docxTreeElement = docxFirstAtom.contentElement;
            const docxParagraph = docxTreeElement.ancestorsAndSelf(W.p).firstOrDefault();
            let previousParagraphInfo: ParagraphInfo | null = null;
            const docxPreviousParagraph = docxParagraph.elementsBeforeSelf(W.p).lastOrDefault();
            if (docxPreviousParagraph !== null) {
                let previousParagraphGuid = getDocugamiIdFromPElement(docxPreviousParagraph);
                if (!!previousParagraphGuid) {
                    previousParagraphInfo = this.paragraphInfoList[previousParagraphGuid];
                }
            }

            const pi = new ParagraphInfo();
            pi.guid = pairs.Key.trim();
            pi.wpElement = docxParagraph;

            const tokenList = Enumerable.from(tokens)
                .select((pair: any) => {
                    const p = pair as TokenParagraphPair;
                    return p.token;
                })
                .toArray();

            pi.comparisonUnitTokenList = tokenList;
            pi.previousParagraphInfo = previousParagraphInfo;
            this.paragraphInfoList[pi.guid as string] = pi;
        }
    }

    // for every correlated sequence range of equal
    //   for every pair of tokens in that range
    //     in the DOCX token, set the otherSideToken field
    //     in the CPML token, set the otherSideToken field
    public correlateTokensBetweenDocxAndDgml()
    {
        const correlatedSequenceList: CorrelatedSequence[] = this.correlatedSequenceList as CorrelatedSequence[];

        // commented out code enables looking at correlated tokens
        // const sb = new StringBuilder();
        for (let i = 0; i < correlatedSequenceList.length; ++i)
        {
            const correlatedSequence = correlatedSequenceList[i];
            if (correlatedSequence.correlationStatus === CorrelationStatus.Equal)
            {
                const pairs = Enumerable.from(correlatedSequence.comparisonUnitArray1)
                    .zip(Enumerable.from(correlatedSequence.comparisonUnitArray2),
                    (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) =>
                    {
                        return {
                            pu1: pu1,
                            pu2: pu2,
                        };
                    })
                    .toArray();
                for (let j = 0; j < pairs.length; ++j)
                {
                    const dgmlToken = pairs[j].pu2 as ComparisonUnitToken;

                    // example code that shows how to get the dgml spanId

                    // const dgmlFirstAtom = dgmlToken.contents[0] as ComparisonUnitAtom;
                    // const dgmlTreeElement = dgmlFirstAtom.contentElement;
                    // const dgmlSpanIdAtt = dgmlTreeElement.attribute(DgXmlNames.spanId);
                    // let dgmlSpanId = "";
                    // if (dgmlSpanIdAtt !== null)
                    // {
                    //     dgmlSpanId = dgmlSpanIdAtt.value;
                    // }

                    const docxToken = pairs[j].pu1 as ComparisonUnitToken;

                    // example code that shows how to get to the w:p element

                    // const docxFirstAtom = docxToken.contents[0] as ComparisonUnitAtom;
                    // const docxTreeElement = docxFirstAtom.contentElement;
                    // const docxParagraphElement = docxTreeElement.ancestorsAndSelf(W.p).first();
                    // const docxParaUuidAtt = docxParagraphElement.attribute(DgXmlNames.uuid)
                    // let docxParaUuid = "";
                    // if (docxParaUuidAtt !== null)
                    // {
                    //     docxParaUuid = docxParaUuidAtt.value;
                    // }

                    docxToken.correspondingToken = dgmlToken;
                    dgmlToken.correspondingToken = docxToken;

                    // sb.appendLine(`Para: ${docxParaUuid}  Dgml: ${dgmlSpanId}  TokenString: >${docxToken.tokenString}<`);
                }
            }
        }

        // sb.appendLine("Correlating tokens");
        // sb.appendLine("==================");
        // Logger.LogDebug(LoggerCategory.AssistedAuthoring, sb.toString());
    }

    public setDgmlSpanIdMetadataForAllParagraphs()
    {
        const openXml = this.openXml;
            const body = openXml
            .descendants(W.body)
            .firstOrDefault();

        const paras = body
            .descendants(W.p)
            .where((para: any) => !para.ancestors(W.sdtContent).any()) // do not add fields as children of content controls - this is invalid.
            .toArray();

        for (let i = 0; i < paras.length; ++i)
        {
            const para = paras[i];
            const paragraphMetadata = getParagraphMetadataFromPElement(para);
            if (!!paragraphMetadata)
            {
                const uuid = paragraphMetadata.ParagraphGuid;
                const chunkType = paragraphMetadata.KnownChunkType as DgKnownChunkType;
                if (!!uuid && chunkType === DgKnownChunkType.Matched)
                {
                    const pi = this.paragraphInfoList[uuid];
                    if (!!pi && !!pi.comparisonUnitTokenList)
                    {
                        const tokenList = pi.comparisonUnitTokenList;
                        const firstDgmlToken: ComparisonUnitToken = tokenList[0].correspondingToken as ComparisonUnitToken;
                        const lastDgmlToken: ComparisonUnitToken = tokenList[tokenList.length - 1].correspondingToken as ComparisonUnitToken;
                        if (!!firstDgmlToken && !!lastDgmlToken)
                        {
                            if (firstDgmlToken.contents.length > 0 && lastDgmlToken.contents.length > 0)
                            {
                                const firstAtomOfFirstToken = firstDgmlToken.contents[0] as ComparisonUnitAtom;
                                const lastAtomOfLastToken = lastDgmlToken.contents[lastDgmlToken.contents.length - 1] as ComparisonUnitAtom;
                                const commonAncestor = firstAtomOfFirstToken
                                    .contentElement
                                    .ancestorsAndSelf()
                                    .firstOrDefault((firstAncestor: any) =>
                                    {
                                        const firstAncestorUuid = firstAncestor.attribute(DgXmlNames.spanId)?.value;
                                        const foundCommon = lastAtomOfLastToken.contentElement.ancestorsAndSelf().any((lastAncestor: any) =>
                                        {
                                            const lastAncestorUuid = lastAncestor.attribute(DgXmlNames.spanId)?.value;
                                            return firstAncestorUuid === lastAncestorUuid;
                                        });
                                        return foundCommon;
                                    });
                                if (!!commonAncestor)
                                {
                                    const spanIdsForParagraph = commonAncestor
                                        .descendantsAndSelf()
                                        .select((d: any) => d.attribute(DgXmlNames.spanId)?.value)
                                        .where((d: string|null) => !!d)
                                        .toArray();
                                    const allSpans = spanIdsForParagraph.join('|').trim('|');
                                    addSpanIdToParagraphMetadata(para, allSpans);
                                }
                                else
                                {
                                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `not found!!`);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    public static async correlateXmlTrees(openXml: any, dgml: any, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<any>
    {
        proxyCallbacks.bucketTimer(3, "100-est/020-corr/010-AttList1");
        const result1 = await createComparisonUnitAtomListFromXml(openXml, true, proxyCallbacks);
        // result1[0] = comparison unit atom list
        // result1[1] = adjusted Open XML whole document markup
        const cal1 = result1[0];
        const root1 = result1[1];

        if (LcsSettings.lcsDebugFalse) {
            const sb = new StringBuilder();
            for (let i = 0; i < cal1.length; ++i)
            {
                sb.appendLine(cal1[i].toString());
            }
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, sb.toString());
        }

        proxyCallbacks.bucketTimer(3, "100-est/020-corr/020-AttList2");
        const result2 = await createComparisonUnitAtomListFromXml(dgml, false, proxyCallbacks);
        // result2[0] = comparison unit atom list for dgml
        // result2[1] = adjusted dgml whole document markup
        const cal2 = result2[0];
        const root2 = result2[1];

        if (LcsSettings.lcsDebugFalse) {
            const sb = new StringBuilder();
            for (let i = 0; i < cal2.length; ++i)
            {
                sb.appendLine(cal2[i].ToString());
            }
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, sb.toString());
        }

        proxyCallbacks.bucketTimer(3, "100-est/020-corr/030-Tokenize1");
        const tok1 = tokenize(cal1);
        proxyCallbacks.bucketTimer(3, "100-est/020-corr/040-Tokenize2");
        const tok2 = tokenize(cal2);

        proxyCallbacks.bucketTimer(3, "100-est/020-corr/100-CreateTokenText1");
        createTokenText(tok1);
        proxyCallbacks.bucketTimer(3, "100-est/020-corr/110-CreateTokenText2");
        createTokenText(tok2);

        if (LcsSettings.lcsDebugFalse) {
            const sb = new StringBuilder();
            for (let i = 0; i < tok1.length; ++i)
            {
                sb.appendLine(tok1[i].toString(0));
            }
            sb.appendLine("====================================");

            for (let i = 0; i < tok2.length; ++i)
            {
                sb.appendLine(tok2[i].toString(0));
            }
            sb.appendLine("====================================");

            Logger.LogDebug(LoggerCategory.AssistedAuthoring, sb.toString());
        }

        proxyCallbacks.bucketTimer(3, "100-est/020-corr/200-Lcs");
        const lcsSettings = new LcsSettings();
        lcsSettings.detailThreshold = 3;
        lcsSettings.fastFindSearchLength = 12; // used in DoLcsAlgorithm
        lcsSettings.fastFindSearchMatch = 6; // used in DoLcsAlgorithm
        const correlatedSequence: CorrelatedSequence[] = lcs(tok1, tok2, lcsSettings, proxyCallbacks);

        if (LcsSettings.lcsDebugFalse) {
            const sb = new StringBuilder();
            correlatedSequence.forEach(cs => sb.appendLine(cs.toString(false)));
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, sb.toString());
        }

        proxyCallbacks.bucketTimer(3, "100-est/020-corr/300-Should-be-zero");
        return [correlatedSequence, root1, root2];
    }

    // create list of tokens, by paragraph, where the key is the paragraph guid
    public getOoxml(proxyCallbacks: IDgWordCanvasProxyCallbacks): string
    {
        proxyCallbacks.bucketTimer(2, "200-DgWordCanvas-getOoxml/100-clean-array");
        // for now, remove the docugami (ns = 'http://docugami.com/2020-06-09') attributes from the tree
        const descendantsArray = this
            .openXml
            .descendantsAndSelf()
            .toArray();

        const dgNs = new XNamespace('http://docugami.com/2020-06-09');
        for (let i = 0; i < descendantsArray.length; ++i)
        {
            const attArray = descendantsArray[i].attributes().where((a: any) => a.name.namespace === dgNs).toArray();
            for (let j = 0; j < attArray.length; ++j)
                attArray[j].remove();
        }

        proxyCallbacks.bucketTimer(2, "200-DgWordCanvas-getOoxml/200-Ltxml-toString");
        return this.openXml.toString();
    }

    /*
    // This is sample markup for Open XML
    // It has no semantic impact.  Put here for documentation purposes.
    <w:r>
        <w:rPr>
            <w:shd w:fill="ab23df00"/>
        </w:rPr>
        <w:t xml:space="preserve">one </w:t>
    </w:r>
    */
    public static async paintContentControls(dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks)
    {
        // get paragraphs with hidden guids
        const OOXML = await proxyCallbacks.getBodyOoxml();
        const root = XElement.parse(OOXML);

        const contentControls = root
            .descendants(W.sdt)
            .toArray();

        for (let i = 0; i < contentControls.length; ++i)
        {
            const runs = contentControls[i].descendants(W.r).toArray();
            for (let j = 0; j < runs.length; ++j)
            {
                const run = runs[j];
                let rPr = run.element(W.rPr);
                if (rPr != null) {
                    rPr.add(
                        new XElement(W.shd,
                            new XAttribute(W.fill, "A0B7E1"))
                    );
                }
                else {
                    rPr = new XElement(W.rPr,
                        new XElement(W.shd,
                            new XAttribute(W.fill, "A0B7E1")));
                    run.addFirst(rPr);
                }
            }
        }

        await proxyCallbacks.replaceBodyOoxml(root.toString());
    }

    /*
    // This is sample markup for Open XML
    // It has no semantic impact.  Put here for documentation purposes.
    <w:r>
        <w:rPr>
            <w:shd w:fill="ab23df00"/>
        </w:rPr>
        <w:t xml:space="preserve">one </w:t>
    </w:r>
    */
    // for every correlated sequence range of equal
    //   for every pair of tokens in that range
    //     in the DOCX token, set the otherSideToken field
    //     in the CPML token, set the otherSideToken field
    //     get the depth of CPML
    //     find the run
    //     add formatting
    // correlation contains pointers into elements in the flatOpc XML tree.
    // paintCanvas does tree modification on that XML tree.
    public paintCanvas()
    {
        const correlatedSequenceList = this.correlatedSequenceList as CorrelatedSequence[];
        //gradient blue array
        // const shadeArray: string[] = [
        //     "FFFFFF",
        //     "F3F5FB",
        //     "E7EDF7",
        //     "DBE4F4",
        //     "CFDBF0",
        //     "C4D2EC",
        //     "B8C9E8",
        //     "ACC0E5",
        //     "A0B7E1",
        //     "95AFDD",
        //     "89A6DA",
        //     "7D9DD6",
        //     "7294D2",
        //     "668BCF",
        //     "5A82CB",
        //     "4E79C7",
        //     "4472C4",
        // ];

        const shadeArray: string[] = [
            "EEEE40",
            "EECF40",
            "F0C332",
            "F3AB17",
            "F2920B",
            "EC7313",
            "E25C26",
            "D34838",
            "BF3B49",
            "AB3059",
            "96295F",
            "7F2064",
            "6A1868",
            "561168",
            "3F0B64",
            "2D0A50",
            "130B36",
        ];

        let minDepth = 99;
        let maxDepth = 0;

        for (let i = 0; i < correlatedSequenceList.length; ++i)
        {
            const correlatedSequence = correlatedSequenceList[i];
            if (correlatedSequence.correlationStatus === CorrelationStatus.Equal) {
                const pairs = Enumerable.from(correlatedSequence.comparisonUnitArray1)
                    .zip(Enumerable.from(correlatedSequence.comparisonUnitArray2),
                    (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) =>
                    {
                        return {
                            pu1: pu1,
                            pu2: pu2,
                        };
                    })
                    .toArray();
                for (let j = 0; j < pairs.length; ++j)
                {
                    const dgmlToken = pairs[j].pu2 as ComparisonUnitToken;
                    const dgmlFirstAtom = dgmlToken.contents[0] as ComparisonUnitAtom;
                    const dgmlTreeElement = dgmlFirstAtom.contentElement;
                    let depth = 1;
                    if (dgmlTreeElement.parent !== null) {
                        depth = dgmlTreeElement.ancestorsAndSelf().count();
                    }
                    if (depth < minDepth) {
                        minDepth = depth;
                    }
                    if (depth > maxDepth) {
                        maxDepth = depth;
                    }
                }
            }
        }
        for (let i = 0; i < correlatedSequenceList.length; ++i)
        {
            const correlatedSequence = correlatedSequenceList[i];
            if (correlatedSequence.correlationStatus === CorrelationStatus.Equal)
            {
                const pairs = Enumerable.from(correlatedSequence.comparisonUnitArray1)
                    .zip(Enumerable.from(correlatedSequence.comparisonUnitArray2),
                    (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) =>
                    {
                        return {
                            pu1: pu1,
                            pu2: pu2,
                        };
                    })
                    .toArray();
                for (let j = 0; j < pairs.length; ++j)
                {
                    const dgmlToken = pairs[j].pu2 as ComparisonUnitToken;

                    const dgmlFirstAtom = dgmlToken.contents[0] as ComparisonUnitAtom;
                    const dgmlTreeElement = dgmlFirstAtom.contentElement;
                    let depth = 1;
                    if (dgmlTreeElement.parent !== null) {
                        depth = dgmlTreeElement.ancestorsAndSelf().count();
                    }

                    const docxToken = pairs[j].pu1 as ComparisonUnitToken;
                    const docxFirstAtom = docxToken.contents[0] as ComparisonUnitAtom;
                    const docxTreeElement = docxFirstAtom.contentElement;
                    const run = docxTreeElement.ancestorsAndSelf(W.r).firstOrDefault();

                    let thisLevel = depth - minDepth;
                    if (thisLevel === 0) {
                        thisLevel = 0;
                    }
                    if (thisLevel > shadeArray.length - 1) {
                        thisLevel = shadeArray.length - 1;
                    }

                    let rPr = run.element(W.rPr);
                    if (rPr != null) {
                        rPr.add(
                            new XElement(W.shd,
                                new XAttribute(W.fill, shadeArray[thisLevel]))
                        );
                    }
                    else {
                        rPr = new XElement(W.rPr,
                            new XElement(W.shd,
                                new XAttribute(W.fill, shadeArray[thisLevel])));
                        run.addFirst(rPr);
                    }
                }
            }
        }
    }

    // currently, the DGML comes formatted, with indenting, so this regex removes that whitespace.
    // the DGML also contains FORMTEXt for fields - remove the token.
    public static dgmlFixup(dgml: string): string
    {
        return dgml
        .replace(/(\\n)/gm,"\n")
        .replace(/(\\r)/gm,"\r")
        .replace(/(\\t)/gm,"\t")
        .replace(/(\\f)/gm,"\f")
        .replace(/(\r\n|\n|\r)\s+</gm," <")
            .replace(/\sFORMTEXT/gm, "");
    }

    public static removeOutflowChunks(dgml: any)
    {
        const chunksToRemove = dgml
            .descendantsAndSelf()
            .where((ch:any) =>
            {
                if (DgWordCanvas.isOutflowChunk(ch))
                {
                    const isSubchunk = ch.ancestors().any((ach:any) => this.isOutflowChunk(ach));
                    if (isSubchunk)
                    {
                        return false;
                    }
                    return true;
                }
            })
            .toArray();
        for (let i = 0; i < chunksToRemove.length; ++i)
        {
            chunksToRemove[i].remove();
        }
    }

    public static isOutflowChunk(ch: any): boolean
    {
        const strAtt = ch.attribute('structure');
        if (strAtt !== null)
        {
            const s = strAtt.value;
            return s.startsWith('outflow_container');
        }
        return false;
    }

    private getHighestAncestorOrSelfSpanIdWithSameStartToken(spanId: string | null): string {
        if (!spanId) {
            return "";
        }

        let chunk = this.dgml
            .descendantsAndSelf(DgXmlNames.chunk)
            .firstOrDefault((ch:any) => ch.attribute(DgXmlNames.spanId)!.value === spanId);
        const fromTok = chunk.attribute(DgXmlNames.fromTok)!.value;
        chunk = chunk
            .ancestorsAndSelf(DgXmlNames.chunk)
            .lastOrDefault((ch: any) => ch.attribute(DgXmlNames.fromTok)!.value === fromTok);
        return chunk.attribute(DgXmlNames.spanId)!.value;
    }
}

export function logChunkBySpanId(dgWordCanvas: DgWordCanvas, functionName: string, idType: string, spanId: string)
{
    if (!!spanId)
    {
        const chunkFromDgml = dgWordCanvas
            .dgml
            .descendantsAndSelf()
            .firstOrDefault((d: any) => d.attribute(DgXmlNames.spanId)?.value === spanId);
        if (!!chunkFromDgml)
        {
            let content = getElementTextValue(chunkFromDgml);
            content = content.replace(/\s+/gm," ");
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, `${functionName} ${idType} ${spanId} content ${content}`);
        }
        else
        {
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, `${functionName} ${idType} ${spanId}, NOT FOUND IN DGML, NO CONTENT`);
        }
    }
    else
    {
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, `${functionName} ${idType} NO SPAN ID, NO CONTENT`);
    }
}

function createTokenText(tokens: ComparisonUnitToken[])
{
    for (let i = 0; i < tokens.length; ++i)
    {
        let str = "";
        let tok = tokens[i];
        for (let j = 0; j < tok.contents.length; ++j)
        {
            const cua = tok.contents[j] as ComparisonUnitAtom;
            str += cua.contentCharacter;
        }
        tok.tokenString = str;
    }
}

enum MachineState {
    InWhiteSpace,
    InToken,
};

function tokenize(cual: ComparisonUnitAtom[]): ComparisonUnitToken[]
{
    let state: MachineState = MachineState.InWhiteSpace;
    const cutl: ComparisonUnitToken[] = []; // this is the token list that this method returns.
    let thisCual: ComparisonUnitAtom[] = []; // this is the temporary list that we are building up, making up a single token.
    for (let i = 0; i < cual.length; ++i)
    {
        const ch = cual[i].contentCharacter;
        if (ch === ' ' || ch.charCodeAt(0) === 160) { // space and non-breaking space
            if (state === MachineState.InWhiteSpace) {
                continue;
            }
            if (state === MachineState.InToken) {
                const cut = new ComparisonUnitToken(thisCual);
                cutl.push(cut);
                thisCual = [];
                state = MachineState.InWhiteSpace;
                continue;
            }
        }
        else
        {
            if (state === MachineState.InWhiteSpace) {
                thisCual = [];
                thisCual.push(cual[i]);
                if (isTokenBreakingCharacter(ch)) {
                    const cut3 = new ComparisonUnitToken(thisCual);
                    cutl.push(cut3);
                    thisCual = [];
                    state = MachineState.InWhiteSpace
                    continue;
                }
                state = MachineState.InToken;
                continue;
            }
            if (state === MachineState.InToken) {
                if (isTokenBreakingCharacter(ch)) {
                    const cut = new ComparisonUnitToken(thisCual);
                    cutl.push(cut);
                    thisCual = [];
                    thisCual.push(cual[i]);
                    const cut2 = new ComparisonUnitToken(thisCual);
                    cutl.push(cut2);
                    thisCual = [];
                    state = MachineState.InWhiteSpace
                    continue;
                }

                const nextContentElement = cual[i].contentElement
                const nextUuidAttValue = nextContentElement.attribute(DgXmlNames.uuid)?.value || "";

                const lastContentElement = thisCual[thisCual.length - 1].contentElement; // we have to have one - we are InToken.
                const lastUuidAttValue = lastContentElement.attribute(DgXmlNames.uuid)?.value || "";

                if (lastUuidAttValue !== nextUuidAttValue) {
                    const cut = new ComparisonUnitToken(thisCual);
                    cutl.push(cut);
                    thisCual = [];
                    thisCual.push(cual[i]);
                    // leave state = InToken
                    continue;
                }
                thisCual.push(cual[i]);
                continue;
            }
        }
    }
    if (state === MachineState.InToken) {
        const cut = new ComparisonUnitToken(thisCual);
        cutl.push(cut);
    }
    return cutl;
}

async function createComparisonUnitAtomListFromXml(xml:any, isOpenXml: boolean, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<any>
{
    const cual: ComparisonUnitAtom[] = [];

    let root = xml;

    // Note: the following works for Word client, but if used on Word online, causes content control creation to fail.
    // tokenize is set to true for DOCX, false for DGML.
    if (LcsSettings.lcsDebugFalse) {
        if (isOpenXml) {
            let newRoot = normalizeRunsTransform(root);
            root = newRoot; // throw away the old tree

            let newRoot2 = tokenizeRunsTransform(root);
            root = newRoot2; // throw away the old tree
        }
    }

    if (isOpenXml)
    {
        const body = root.descendants(W.body).firstOrDefault();
        if (body !== null)
        {
            await annotateElementsWithUuid(body, proxyCallbacks);
            createComparisonUnitAtomListRecurse(body, cual);
        }
        else
        {
            // this should never be executed.  There never should be an Open XML document that
            // does not have a body element.
            const message = "Fatal error - Open XML document does not have a body element";
            Logger.LogCritical(LoggerCategory.AssistedAuthoring, message);
            console.error(message);
        }
    }
    else
    {
        await annotateElementsWithUuid(root, proxyCallbacks);
        createComparisonUnitAtomListRecurse(root, cual);
    }

    if (LcsSettings.lcsDebugFalse)
    {
        const sb = new StringBuilder();
        cual.forEach(cua => {
            sb.appendLine(cua.toString());
        });
        Logger.LogDebug(LoggerCategory.AssistedAuthoring, sb.toString());
    }

    return [cual, root];
};

function addHiddenParagraphUuids(body: any)
{
    const paras = body
        .descendants(W.p)
        .where((para: any) => !para.ancestors(W.sdtContent).any()) // do not add fields as children of content controls - this is invalid.
        .toArray();

    for (let i = 0; i < paras.length; ++i)
    {
        const para = paras[i];
        const uuid = para.attribute(DgXmlNames.uuid)?.value || "";

        const paragraphMetadata = getParagraphMetadataFromPElement(para);
        if (!!paragraphMetadata)
        {
            const spanType = paragraphMetadata.KnownChunkType as DgKnownChunkType;
            if (spanType === DgKnownChunkType.Matched) // don't update if DOCUINSE OR DOCUREPL
            {
                updateDocugamiIdInPElement(para, uuid);
            }
        }
        else
        {
            updateDocugamiIdInPElement(para, uuid);
        }
    }
}

function tokenizeRunsTransform(node: any): any
{
    let element = null;
    if (node.nodeType === 'Element') {
        element = node;
    }
    if (element !== null) {
        if (element.name === W.r) {
            if (element.element(W.t) === null) {
                const clone = new XElement(element);
                return clone;
            }

            const rpr = element.element(W.rPr);
            const t = element.element(W.t);
            let sb = '';
            const na = t.nodes().toArray();
            for (let i = 0; i < na.length; ++i)
            {
                if (na[i].nodeType === 'Text') {
                    sb += na[i].value;
                }
                else if (na[i].nodeType === 'Entity') {
                    const toString = na[i].toString();
                    const doc = new DOMParser().parseFromString("<p>" + toString + "</p", "text/html");
                    const ch = doc.documentElement.textContent as string;
                    sb += ch;
                }
            }

            const tokenStrings = tokenizeString(sb);

            // project runs for each item in tokenStrings
            const newRuns = Enumerable.from(tokenStrings)
                .select((tok: string) =>
                {
                    const newRun = new XElement(W.r,
                        rpr,
                        new XElement(W.t,
                            new XAttribute(XNamespace.xml + "space", "preserve"),
                            tok));
                    return newRun;
                })
                .toArray();
            return newRuns;
        }

        return new XElement(element.name,
            element.attributes(),
            element.nodes().select((n: any) => tokenizeRunsTransform(n)));
    }
    return node;
}

export function tokenizeString(tValue: string): string[]
{
    let state: MachineState = MachineState.InWhiteSpace;
    let tokenList: string[] = []; // this is the token list that this method returns.
    let thisToken: string = ""; // this is the temporary list that we are building up, making up a single token.
    for (let i = 0; i < tValue.length; ++i)
    {
        const ch = tValue.charAt(i);
        if (ch === ' ' || ch.charCodeAt(0) === 160) { // space and non-breaking space
            if (state === MachineState.InWhiteSpace) {
                thisToken += ch;
                continue;
            }
            if (state === MachineState.InToken) {
                thisToken += ch;
                tokenList.push(thisToken);
                thisToken = "";
                state = MachineState.InWhiteSpace;
                continue;
            }
        }
        else
        {
            if (state === MachineState.InWhiteSpace) {
                thisToken += tValue.charAt(i);
                if (isTokenBreakingCharacter(ch)) {
                    tokenList.push(thisToken);
                    thisToken = "";
                    state = MachineState.InWhiteSpace
                    continue;
                }
                state = MachineState.InToken;
                continue;
            }
            if (state === MachineState.InToken) {
                if (isTokenBreakingCharacter(ch)) {
                    tokenList.push(thisToken);
                    tokenList.push(tValue.charAt(i));
                    thisToken = "";
                    state = MachineState.InWhiteSpace
                    continue;
                }

                thisToken += tValue.charAt(i);
                continue;
            }
        }
    }
    if (thisToken.length !== 0) {
        tokenList.push(thisToken);
    }
    return tokenList;
}


function normalizeRunsTransform(node: any): any
{
    let element = null;
    if (node.nodeType === 'Element') {
        element = node;
    }
    if (element !== null) {
        if (element.name === W.r) {
            const rpr = element.element(W.rPr);
            const runAtts = element.attributes().toArray();
            const children = element.elements().where((e: any) => e.name !== W.rPr);

            return children.select((c: any) =>
                new XElement(W.r,
                    runAtts,
                    rpr,
                    c));
        }

        return new XElement(element.name,
            element.attributes(),
            element.nodes().select((n: any) => normalizeRunsTransform(n)));
    }
    return node;
}

async function annotateElementsWithUuid(root: any, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<void>
{
    const elements: Array<any> = [];

    root.descendantsAndSelf().forEach((d: any) =>
    {
        elements.push(d);
    });

    for (const element of elements)
    {
        const uuid = await proxyCallbacks.getGuid();
        const id = uuid.replace(/-/g, "");
        element.add(new XAttribute(DgXmlNames.uuid, id));
    }

    return Promise.resolve();
}

const charactersToElide: string[] = [
    '\n',
    '\u25cf' // black circle
];

function createComparisonUnitAtomListRecurse(node:any, cual: ComparisonUnitAtom[])
{
    if (node.nodeType === 'Element' && node.name === W.instrText) {
        return;
    }

    if (node.nodes)
    {
        if (node.nodes().any((n: any) => (n.nodeType === 'Text' || n.nodeType === 'Entity')))
        {
            const childNodes = node.nodes().toArray();
            for (let i = 0; i < childNodes.length; ++i)
            {
                if (childNodes[i].nodeType === 'Text')
                {
                    const val = childNodes[i].value;
                    for (let j = 0; j < val.length; j++) {
                        const ch = val.charAt(j);
                        if (! charactersToElide.includes(ch))
                        {
                            const cua = new ComparisonUnitAtom(childNodes[i].parent, ch);
                            cual.push(cua);
                        }
                    }
                }
                else if (childNodes[i].nodeType === 'Entity')
                {
                    const ent = childNodes[i];
                    const toString = ent.toString();
                    const doc = new DOMParser().parseFromString("<p>" + toString + "</p>", "text/html");
                    const ch = doc.documentElement.textContent as string;
                    const cua = new ComparisonUnitAtom(childNodes[i].parent, ch);
                    cual.push(cua);
                }
                else
                {
                    createComparisonUnitAtomListRecurse(childNodes[i], cual);
                }
            }
            return;
        }
    }
    if (node.nodeType === 'Element')
    {
        const nodeArray = node.nodes().toArray();
        for (let idx = 0; idx < nodeArray.length; ++idx)
        {
            createComparisonUnitAtomListRecurse(nodeArray[idx], cual);
        }
    }
};

